import { memo, useCallback, useMemo } from 'react';

import { useTrackAnnouncementActionClicked } from '@air/analytics';
import { NotificationsList } from '@air/component-notifications';
import {
  type AnnouncementAction,
  type AnnouncementCommand,
  isAnnouncementCommandAction,
} from '@air/feature-announcements';
import { Button } from '@air/primitive-button';
import { Spinner } from '@air/primitive-spinner';

import { AnnouncementNewsRow } from '~/components/AirNotifications/components/AnnouncementNewsRow';
import { AnnouncementsNewsEmpty } from '~/components/AirNotifications/components/AnnouncementsNewsEmpty';
import { openAnnouncementLink } from '~/components/announcements/openAnnouncementLink';
import { useOpenBlankCanvasAnnouncementCommand } from '~/components/announcements/useOpenBlankCanvasAnnouncementCommand';
import { type PrivateAnnouncementListItem } from '~/components/v2/hooks/queries/usePrivateAnnouncementsList';
import { usePrivateAnnouncementsNewsList } from '~/components/v2/hooks/queries/usePrivateAnnouncementsNewsList';

// The shared row is unclamped (links/buttons need room), so it typically runs taller than a standard notification row.
const ANNOUNCEMENT_NEWS_ITEM_ESTIMATE_HEIGHT_PX = 140;

const NEWS_SUPPORTED_COMMANDS: readonly AnnouncementCommand[] = ['openBlankCanvas'];

export const ANNOUNCEMENTS_NEWS_ERROR_TEST_ID = 'ANNOUNCEMENTS_NEWS_ERROR';
export const ANNOUNCEMENTS_NEWS_RETRY_TEST_ID = 'ANNOUNCEMENTS_NEWS_RETRY';
export const ANNOUNCEMENTS_NEWS_RETRY_NEXT_PAGE_TEST_ID = 'ANNOUNCEMENTS_NEWS_RETRY_NEXT_PAGE';

export const AnnouncementsNewsList = memo(() => {
  const {
    announcements,
    fetchNextPage,
    hasNextPage,
    isError,
    isFetchNextPageError,
    isFetching,
    isFetchingNextPage,
    isLoading,
    refetch,
  } = usePrivateAnnouncementsNewsList();
  const { canOpenBlankCanvas, openBlankCanvas } = useOpenBlankCanvasAnnouncementCommand();
  const supportedCommands = canOpenBlankCanvas ? NEWS_SUPPORTED_COMMANDS : undefined;
  const { trackAnnouncementActionClicked } = useTrackAnnouncementActionClicked();

  const handleAction = useCallback(
    (announcement: PrivateAnnouncementListItem, action: AnnouncementAction) => {
      trackAnnouncementActionClicked({
        action_label: action.label,
        action_type: action.type,
        announcement_id: announcement.id,
        name: announcement.name,
        surface: 'news',
      });

      if (isAnnouncementCommandAction(action)) {
        if (action.command === 'openBlankCanvas') {
          openBlankCanvas(announcement.id);
        }

        return;
      }

      openAnnouncementLink(action.url);
    },
    [openBlankCanvas, trackAnnouncementActionClicked],
  );

  const listItems = useMemo(
    () =>
      announcements.map((announcement) => ({
        announcement,
        id: announcement.id,
      })),
    [announcements],
  );

  const getEstimateSize = useCallback(() => ANNOUNCEMENT_NEWS_ITEM_ESTIMATE_HEIGHT_PX, []);

  const handleRetryNextPage = useCallback(() => {
    void fetchNextPage();
  }, [fetchNextPage]);

  const handleRetry = useCallback(() => {
    void refetch();
  }, [refetch]);

  const renderLoader = useCallback(() => {
    if (isFetchNextPageError) {
      return (
        <div className="flex items-center justify-center py-4">
          <Button
            appearance="outline"
            color="gray"
            data-testid={ANNOUNCEMENTS_NEWS_RETRY_NEXT_PAGE_TEST_ID}
            disabled={isFetching}
            onClick={handleRetryNextPage}
            size="small"
          >
            Load more
          </Button>
        </div>
      );
    }

    return (
      <div className="flex items-center justify-center py-4 ads:text-ads-cyan-10 text-teal-9">
        <Spinner />
      </div>
    );
  }, [handleRetryNextPage, isFetchNextPageError, isFetching]);

  const renderNotification = useCallback(
    ({ notification }: { notification: { announcement: PrivateAnnouncementListItem } }) => (
      <AnnouncementNewsRow
        announcement={notification.announcement}
        onAction={handleAction}
        supportedCommands={supportedCommands}
      />
    ),
    [handleAction, supportedCommands],
  );

  const renderNullState = useCallback(() => <AnnouncementsNewsEmpty />, []);

  if (isLoading) {
    return (
      <div className="flex h-full items-center justify-center py-10 ads:text-ads-cyan-10 text-teal-9">
        <Spinner />
      </div>
    );
  }

  if (isError && !announcements.length) {
    return (
      <div className="flex h-full flex-col items-center justify-center gap-2 px-6 py-10 text-center">
        <p
          className="ads:text-metadata text-14 text-gray-10"
          data-testid={ANNOUNCEMENTS_NEWS_ERROR_TEST_ID}
          role="alert"
        >
          Couldn't load news.
        </p>
        <Button
          appearance="outline"
          color="gray"
          data-testid={ANNOUNCEMENTS_NEWS_RETRY_TEST_ID}
          disabled={isFetching}
          onClick={handleRetry}
          size="small"
        >
          Try again
        </Button>
      </div>
    );
  }

  return (
    <NotificationsList
      getEstimateSize={getEstimateSize}
      hasNextPage={hasNextPage}
      // The list auto-fetches whenever its last row is visible and no fetch is running; a failed page must wait for "Load more".
      isFetchingNextPage={isFetchingNextPage || isFetchNextPageError}
      notifications={listItems}
      onFetchNextPage={fetchNextPage}
      renderLoader={renderLoader}
      renderNotification={renderNotification}
      renderNullState={renderNullState}
    />
  );
});

AnnouncementsNewsList.displayName = 'AnnouncementsNewsList';
